// Optimized Main Index Component
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Rabbit from "rabbit-node/lib/rabbit";
import { Box, Button, Typography, Divider } from "@mui/material";
import useModal from "../../../hooks/useModel";
import { FaAngleRight } from "react-icons/fa6";

// loading state
import Loading from "../../../components/loading";
import AudioDetailLoading from "../../../components/skeletonLoading/audioDetailLoading/AudioDetailLoading";

import FlatList from "../../../components/FlatList";

import { useAudio } from "../../../context/AudioContext";

// Resuable Components
import ImageWithTag from "../../../components/reusable/imageWithTag/ImageWithTag";

// custom context
import { useNavbar } from "../../../context/NavBarContext";
import { useFetchAudiobookByDetailDataQuery } from "../../../api/audioBookByDetailPageApi";

import { NotFound } from "../../error/NotFound_404";
import { useFetchAudioStreamingDataQuery } from "../../../api/audioStreamingPageApi";
import AudioPlayer from "../../../components/reusable/player/AudioPlayer";
import { IoChevronBack } from "react-icons/io5";
import { Moon, Pause, Play, SkipBack, SkipForward, Zap } from "lucide-react";

const Index = () => {
  const location = useLocation();
  const audioContext = useAudio();
  const navigate = useNavigate();
  const Navbar = useNavbar();
  const [Modal, openModal, closeModal] = useModal();
  const [SaleModal, openSaleModal, closeSaleModal] = useModal(); // New modal for sale items
  const detail_id = useParams().item_id;
  
  // Single state to track streaming status
  const [shouldFetchStreaming, setShouldFetchStreaming] = useState(false);
  const [streamingState, setStreamingState] = useState({
    data: null,
    isLoading: false,
    hasRequested: false,
    error: null
  });

  // Ref to prevent multiple simultaneous requests
  const streamingRequestRef = useRef(false);
  // Promise resolver for streaming data
  const streamingPromiseRef = useRef(null);

  useLayoutEffect(() => {
    Navbar.setTogglePlace(<IoChevronBack fontSize="20" />);
  }, []);

  const { data, error, isLoading, isFetching } =
    useFetchAudiobookByDetailDataQuery(detail_id);

  const [sleepMode, setSleepMode] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [sleepTimer, setSleepTimer] = useState(30);

  // Check if data.sale == 1 and show modal
  useEffect(() => {
    if (data && (data.sale == 1 && data.price == "Premium" && data.is_purchase == 0)) {
      openSaleModal();
    }
  }, [data, openSaleModal]);

  // Use existing query with skip parameter for better control
  const {
    data: streamingData,
    error: streamingError,
    isLoading: streamingLoading,
    isFetching: streamingFetching,
    refetch: refetchStreamingData,
  } = useFetchAudioStreamingDataQuery(
    { id: detail_id },
    { skip: !shouldFetchStreaming }
  );

  const toggleSpeed = () => {
    const speeds = [1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    setPlaybackSpeed(speeds[(currentIndex + 1) % speeds.length]);
  };

  // Handle streaming data when it arrives
  useEffect(() => {
    if (streamingData && streamingState.hasRequested) {
      // console.log("Streaming data received:", streamingData);
      
      setStreamingState(prev => ({
        ...prev,
        data: streamingData,
        isLoading: false,
        error: null
      }));

      // Resolve any pending promise
      if (streamingPromiseRef.current) {
        const audioUrl = streamingData.streaming_link || streamingData.download_link;
        
        if (!audioUrl) {
          console.log("No streaming links available, opening modal");
          openModal();
          streamingPromiseRef.current.resolve(null);
        } else {
          streamingPromiseRef.current.resolve(audioUrl);
        }
        
        streamingPromiseRef.current = null;
      }

      // Check if streaming links are empty
      if (!streamingData?.streaming_link && !streamingData?.download_link) {
        console.log("No streaming links available, opening modal");
        openModal();
      }
    }
  }, [streamingData, streamingState.hasRequested]); //openModal

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && streamingState.hasRequested) {
      console.error("Streaming error:", streamingError);
      setStreamingState(prev => ({
        ...prev,
        isLoading: false,
        error: streamingError.message || "Failed to fetch streaming data"
      }));

      // Reject any pending promise
      if (streamingPromiseRef.current) {
        streamingPromiseRef.current.resolve(null);
        streamingPromiseRef.current = null;
      }
    }
  }, [streamingError, streamingState.hasRequested]);

  // **OPTIMIZED STREAMING DATA FETCH FUNCTION - FIXED FOR SINGLE CLICK**
  const handleFetchStreamingData = async () => {
    // Prevent multiple simultaneous requests
    if (streamingRequestRef.current || streamingState.isLoading) {
      console.log("Streaming request already in progress, ignoring...");
      return null;
    }

    // If we already have valid streaming data, return it immediately
    if (streamingState.data && streamingState.data.audio_id === data?.audio_id) {
      console.log("Using cached streaming data");
      const audioUrl = streamingState.data.streaming_link || streamingState.data.download_link;
      return audioUrl;
    }

    console.log("Fetching streaming data for audio player...");
    
    // Set request in progress
    streamingRequestRef.current = true;
    setStreamingState(prev => ({
      ...prev,
      isLoading: true,
      hasRequested: true,
      error: null
    }));

    try {
      if (shouldFetchStreaming) {
        // If already enabled, refetch manually
        console.log("Refetching streaming data...");
        const result = await refetchStreamingData();
        
        if (result?.data) {
          setStreamingState(prev => ({
            ...prev,
            data: result.data,
            isLoading: false,
            error: null
          }));
          
          if (!result.data.streaming_link && !result.data.download_link) {
            openModal();
            return null;
          }
          
          const audioUrl = result.data.streaming_link || result.data.download_link;
          return audioUrl;
        }
        return null;
      } else {
        // Enable fetching for the first time and wait for result via Promise
        console.log("Enabling streaming fetch and waiting for data...");
        setShouldFetchStreaming(true);
        
        // Create a promise that will be resolved when data arrives
        return new Promise((resolve) => {
          streamingPromiseRef.current = { resolve };
          
          // Set a timeout to prevent hanging
          setTimeout(() => {
            if (streamingPromiseRef.current) {
              console.log("Timeout waiting for streaming data");
              streamingPromiseRef.current.resolve(null);
              streamingPromiseRef.current = null;
            }
          }, 10000); // 10 second timeout
        });
      }
      
    } catch (error) {
      console.error("Error fetching streaming data:", error);
      setStreamingState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || "Failed to fetch streaming data"
      }));
      return null;
    } finally {
      // Reset request flag
      streamingRequestRef.current = false;
    }
  };

  const handleClickRelatedItem = (item) => {
    // Reset streaming state when navigating to new item
    setShouldFetchStreaming(false);
    setStreamingState({
      data: null,
      isLoading: false,
      hasRequested: false,
      error: null
    });
    streamingRequestRef.current = false;
    
    // Clean up any pending promise
    if (streamingPromiseRef.current) {
      streamingPromiseRef.current.resolve(null);
      streamingPromiseRef.current = null;
    }
    
    item && navigate(`/audio/book-detail/${item?.uniq_idx}`, { replace: true });
  };

  // Reset streaming state when detail_id changes (new episode)
  useEffect(() => {
    setShouldFetchStreaming(false);
    setStreamingState({
      data: null,
      isLoading: false,
      hasRequested: false,
      error: null
    });
    streamingRequestRef.current = false;
    
    // Clean up any pending promise
    if (streamingPromiseRef.current) {
      streamingPromiseRef.current.resolve(null);
      streamingPromiseRef.current = null;
    }
  }, [detail_id]);

  // Set playback speed when it changes
  useEffect(() => {
    if (audioContext.audioRef.current) {
      audioContext.audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  if (isLoading) {
    return <AudioDetailLoading />;
  }

  if (error) {
    return <NotFound error={error} />;
  }

  const handleSubscribeConfirmed = () => {
    navigate("/account/purchase_subscription");
    closeModal();
  };

  const handleCanceled = () => {
    closeModal();
  };

  // Handle sale modal actions - both OK and Cancel redirect back
  const handleSaleModalOk = () => {
    closeSaleModal();
    navigate(-1); // Go back to previous page
  };

  const handleSaleModalCancel = () => {
    closeSaleModal();
    navigate(-1); // Go back to previous page
  };

  const modalBody = (
    <div className="text-center leading-5">
      <Typography
        variant="body1"
        component="div"
        lineHeight={2}
        fontWeight="bold"
      >
        အကန့်သတ်မရှိ
      </Typography>
      <Typography
        variant="body1"
        component="div"
        lineHeight={2}
        fontWeight="bold"
      >
        အသံစာအုပ်နှင့်သီချင်းများစွာ
      </Typography>
      <Typography
        variant="body1"
        component="div"
        lineHeight={2}
        fontWeight="bold"
      >
        နားဆင်ဖို့ အခုပဲ ဝန်ဆောင်မှုရယူလိုက်ပါ
      </Typography>
    </div>
  );

  const modalActions = (
    <div className="flex items-center justify-center w-full mb-3">
      <Button
        size="small"
        variant="contained"
        disableRipple
        onClick={handleSubscribeConfirmed}
        sx={{
          marginTop: "20px",
          paddingTop: "5px",
          paddingBottom: "5px",
          paddingRight: "50px",
          paddingLeft: "50px",
          borderTopLeftRadius: "10px",
          borderBottomLeftRadius: "10px",
          borderTopRightRadius: "10px",
          borderBottomRightRadius: "10px",
          backgroundColor: "#facc15",
          color: "black",
          fontWeight: "600",
          "&:hover": {
            backgroundColor: "#eab308",
            borderColor: "#eab308",
            boxShadow: "none",
          },
          "&:active": {
            boxShadow: "none",
            backgroundColor: "#facc15",
            borderColor: "#ca8a04",
          },
        }}
      >
        ဝန်ဆောင်မှုရယူမည်
      </Button>
    </div>
  );

  // Sale modal content
  const saleModalBody = (
    <div className="text-center leading-5">
      <Typography
        variant="body1"
        component="div"
        lineHeight={2}
        fontWeight="bold"
      >
        ယခုစာအုပ်မှာ အရောင်းသီးသန့်စာအုပ်ဖြစ်ပါသည်။ စာအုပ်ကို ဝယ်ယူရန်လိုအပ်ပါသည်။
      </Typography>
    </div>
  );

  const saleModalActions = (
    <div className="flex items-center justify-center gap-4 w-full mb-3">
      <Button
        size="small"
        variant="outlined"
        disableRipple
        onClick={handleSaleModalCancel}
        sx={{
          marginTop: "20px",
          paddingTop: "8px",
          paddingBottom: "8px",
          paddingRight: "20px",
          paddingLeft: "20px",
          borderRadius: "20px",
          borderColor: "#6b7280",
          color: "#6b7280",
          fontWeight: "600",
          "&:hover": {
            backgroundColor: "rgba(107, 114, 128, 0.1)",
            borderColor: "#6b7280",
          },
        }}
      >
        Cancel
      </Button>
      <Button
        size="small"
        variant="contained"
        disableRipple
        onClick={handleSaleModalOk}
        sx={{
          marginTop: "20px",
          paddingTop: "8px",
          paddingBottom: "8px",
          paddingRight: "30px",
          paddingLeft: "30px",
          borderRadius: "20px",
          backgroundColor: "#facc15",
          color: "black",
          fontWeight: "600",
          "&:hover": {
            backgroundColor: "#eab308",
            boxShadow: "none",
          },
          "&:active": {
            boxShadow: "none",
            backgroundColor: "#facc15",
          },
        }}
      >
        OK
      </Button>
    </div>
  );

  return (
    <div className="w-full h-full overflow-y-hidden max-w-md mx-auto px-4">
      <div className="w-full relative overflow-hidden">

        {/* Cover Section */}
        <div className="flex items-center justify-center w-full px-4">
          <div className="relative">
            <img 
              src={data.image}
              alt={data.title}
              className="w-48 max-h-[50vh] sm:max-h-[40vh] object-cover rounded-2xl shadow-2xl"
              onError={(e) => { 
                e.target.src = 'https://via.placeholder.com/300x400/FF6B35/FFFFFF?text=Myanmar+Comic'; 
              }}
            />
          </div>
        </div>

        {/* Title Section */}
        <div className="px-4 text-center my-6">
          <p className="text-lg font-bold mb-1">{Rabbit.zg2uni(data.series)}</p>
          <p className="text-blue-200 text-sm">{Rabbit.zg2uni(data.title)}</p>
        </div>

        {/* Audio Player with streamlined props */}
        <AudioPlayer 
          streamingData={streamingState.data || streamingData}
          data={data}
          onFetchStreamingData={handleFetchStreamingData}
          streamingLoading={streamingState.isLoading || streamingLoading}
          userRequestedStreaming={streamingState.hasRequested}
        />

        {/* Bottom Controls */}
        <div className="px-2 pb-8 flex justify-center flex-wrap gap-2">
          <button 
            className={`flex items-center space-x-2 px-4 py-2 rounded-full border-2 transition-all ${
              sleepMode 
                ? 'border-yellow-400 bg-yellow-400 text-black shadow-lg' 
                : 'border-gray-300 border-opacity-50 text-gray-200 hover:border-yellow-400 hover:text-yellow-400'
            }`}
          >
            <Moon size={16} />
            <span className="text-sm font-medium">
              {sleepMode ? `Sleep ${sleepTimer}m` : 'Sleep mode'}
            </span>
          </button>

          <button 
            className="flex items-center space-x-2 px-4 py-2 rounded-full border-2 border-gray-300 border-opacity-50 text-gray-200 hover:border-yellow-400 hover:text-yellow-400 transition-all"
          >
            <Zap size={16} />
            <span onClick={toggleSpeed} className="text-sm font-medium">Speed {playbackSpeed}x</span>
          </button>
        </div>

        {/* Subscription Modal */}
        <Modal
          title=""
          body={modalBody}
          actions={modalActions}
          isCloseCustom={true}
          onCloseCustom={handleCanceled}
        />

        {/* Sale Modal */}
        <SaleModal
          title=""
          body={saleModalBody}
          actions={saleModalActions}
          isCloseCustom={true}
          onCloseCustom={handleSaleModalCancel}
        />
      </div>
    </div>
  );
};

export default Index;